'use client';

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

interface ViatorProduct {
  id: string;
  product_code: string;
  title: string;
  slug: string;
  short_description?: string;
  description?: string;
  duration_label?: string;
  duration_minutes?: number;
  price_from?: number;
  currency_code: string;
  reviews_rating?: number;
  reviews_count: number;
  destination_name?: string;
  city_name?: string;
  country_code?: string;
  images: Array<{ variants?: Array<{ url: string; width: number }> }>;
  is_instant_confirmation: boolean;
  is_free_cancellation: boolean;
  is_private: boolean;
  is_luxury: boolean;
  is_family_friendly: boolean;
  categories: Array<{ name: string }>;
}

interface SearchResult {
  products: ViatorProduct[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const CATEGORIES = [
  'Cultural', 'Adventure', 'Food & Wine', 'Nature', 'Water Activities',
  'Wellness', 'Safari', 'Cruise', 'Walking Tours', 'Family', 'Luxury', 'Private',
];

const SORT_OPTIONS = [
  { value: 'recommended', label: 'Recommended' },
  { value: 'rating', label: 'Highest Rated' },
  { value: 'reviews', label: 'Most Reviewed' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
];

const TRENDING_DESTINATIONS = [
  { name: 'Paris', flag: '🇫🇷', country: 'FR' },
  { name: 'Rome', flag: '🇮🇹', country: 'IT' },
  { name: 'Dubai', flag: '🇦🇪', country: 'AE' },
  { name: 'Tokyo', flag: '🇯🇵', country: 'JP' },
  { name: 'New York', flag: '🇺🇸', country: 'US' },
  { name: 'Barcelona', flag: '🇪🇸', country: 'ES' },
  { name: 'London', flag: '🇬🇧', country: 'GB' },
  { name: 'Sydney', flag: '🇦🇺', country: 'AU' },
];

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg key={star} className={`w-3 h-3 ${star <= Math.round(rating) ? 'text-[var(--primary)]' : 'text-[var(--border)]'}`} fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
      <span className="text-xs text-[var(--muted-foreground)] ml-1">{rating.toFixed(1)}</span>
    </div>
  );
}

function ProductCard({ product }: { product: ViatorProduct }) {
  const coverImage = product.images?.[0]?.variants?.find((v) => v.width >= 400)?.url
    ?? product.images?.[0]?.variants?.[0]?.url;

  return (
    <Link href={`/activities/${product.slug}`} className="glass-card rounded-xl overflow-hidden hover:border-[rgba(201,168,76,0.3)] transition-all group block">
      {/* Image */}
      <div className="relative h-44 bg-[var(--surface-3)] overflow-hidden">
        {coverImage ? (
          <img
            src={coverImage}
            alt={product.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-4xl">🌍</span>
          </div>
        )}
        <div className="absolute top-2 left-2 flex flex-wrap gap-1">
          {product.is_instant_confirmation && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/90 text-white font-semibold">Instant</span>
          )}
          {product.is_free_cancellation && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/90 text-white font-semibold">Free Cancel</span>
          )}
          {product.is_private && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[rgba(201,168,76,0.9)] text-black font-semibold">Private</span>
          )}
          {product.is_luxury && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-500/90 text-white font-semibold">Luxury</span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="text-sm font-bold leading-tight group-hover:text-[var(--primary)] transition-colors line-clamp-2" style={{ fontFamily: 'var(--font-serif)' }}>
            {product.title}
          </h3>
        </div>

        {(product.destination_name || product.city_name) && (
          <p className="text-xs text-[var(--muted-foreground)] mb-2 flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {product.city_name ?? product.destination_name}
          </p>
        )}

        {product.reviews_rating && product.reviews_rating > 0 ? (
          <div className="flex items-center gap-2 mb-2">
            <StarRating rating={product.reviews_rating} />
            <span className="text-[10px] text-[var(--muted-foreground)]">({product.reviews_count.toLocaleString()})</span>
          </div>
        ) : null}

        <div className="flex items-center justify-between mt-3">
          <div>
            {product.duration_label && (
              <p className="text-[10px] text-[var(--muted-foreground)]">{product.duration_label}</p>
            )}
          </div>
          {product.price_from ? (
            <div className="text-right">
              <p className="text-[9px] text-[var(--muted-foreground)]">From</p>
              <p className="text-sm font-bold text-[var(--primary)]">
                {product.currency_code} {product.price_from.toLocaleString()}
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </Link>
  );
}

export default function ActivitiesPage() {
  const router = useRouter();
  const [query, setQuery] = useState('');
  const [destination, setDestination] = useState('');
  const [travelDate, setTravelDate] = useState('');
  const [sortBy, setSortBy] = useState('recommended');
  const [priceMax, setPriceMax] = useState('');
  const [isInstant, setIsInstant] = useState(false);
  const [isFreeCancellation, setIsFreeCancellation] = useState(false);
  const [isLuxury, setIsLuxury] = useState(false);
  const [isFamilyFriendly, setIsFamilyFriendly] = useState(false);
  const [page, setPage] = useState(1);
  const [searchResult, setSearchResult] = useState<SearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [viatorConfigured, setViatorConfigured] = useState<boolean | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [isSampleData, setIsSampleData] = useState(false);
  const [syncStatus, setSyncStatus] = useState<{ productCount: number; configured: boolean; lastSyncedAt: string | null } | null>(null);
  const [syncing, setSyncing] = useState(false);

  const fetchProducts = useCallback(async (resetPage = false) => {
    setLoading(true);
    const currentPage = resetPage ? 1 : page;
    if (resetPage) setPage(1);

    const params = new URLSearchParams({
      sortBy,
      page: String(currentPage),
      pageSize: '24',
    });
    if (query) params.set('q', query);
    if (destination) params.set('destination', destination);
    if (priceMax) params.set('priceMax', priceMax);
    if (isInstant) params.set('instant', 'true');
    if (isFreeCancellation) params.set('freeCancellation', 'true');
    if (isLuxury) params.set('luxury', 'true');
    if (isFamilyFriendly) params.set('family', 'true');

    try {
      const res = await fetch(`/api/viator/products?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setSearchResult(data);
        setViatorConfigured(data.viatorConfigured ?? true);
        // isSampleData: true only when source is explicitly 'sample'
        setIsSampleData(data.source === 'sample');
      } else {
        setViatorConfigured(false);
        setIsSampleData(false);
      }
    } catch {
      setViatorConfigured(false);
      setIsSampleData(false);
    } finally {
      setLoading(false);
    }
  }, [query, destination, priceMax, isInstant, isFreeCancellation, isLuxury, isFamilyFriendly, sortBy, page]);

  // Load sync status on mount
  useEffect(() => {
    fetch('/api/viator/catalogue-sync?action=status')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setSyncStatus({ productCount: d.productCount, configured: d.configured, lastSyncedAt: d.lastSyncedAt }); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [page, sortBy]);

  const handleSearch = () => fetchProducts(true);

  const handleDestinationClick = (dest: typeof TRENDING_DESTINATIONS[0]) => {
    setDestination(dest.name);
    fetchProducts(true);
  };

  const handleTriggerSync = async () => {
    setSyncing(true);
    try {
      const res = await fetch('/api/viator/catalogue-sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'incremental', triggeredBy: 'activities-page' }),
      });
      if (res.ok) {
        await fetchProducts(true);
        const statusRes = await fetch('/api/viator/catalogue-sync?action=status');
        if (statusRes.ok) {
          const d = await statusRes.json();
          setSyncStatus({ productCount: d.productCount, configured: d.configured, lastSyncedAt: d.lastSyncedAt });
        }
      }
    } catch { /* ignore */ } finally {
      setSyncing(false);
    }
  };

  const handleBooking = (product: ViatorProduct) => {
    const params = new URLSearchParams({
      vertical: 'activity',
      productCode: product.product_code,
      title: product.title,
    });
    router.push(`/booking?${params.toString()}`);
  };

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)]">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[var(--surface-2)]/95 backdrop-blur-xl border-b border-[var(--border)] h-16 flex items-center px-6">
        <Link href="/" className="text-[var(--primary)] font-bold tracking-widest uppercase mr-8" style={{ fontFamily: 'var(--font-serif)' }}>WorldwayLuxe</Link>
        <div className="hidden md:flex items-center gap-6 text-sm text-[var(--muted-foreground)]">
          <Link href="/flights" className="hover:text-[var(--primary)] transition-colors">Flights</Link>
          <Link href="/hotels" className="hover:text-[var(--primary)] transition-colors">Hotels</Link>
          <Link href="/transfers" className="hover:text-[var(--primary)] transition-colors">Transfers</Link>
          <Link href="/safari" className="hover:text-[var(--primary)] transition-colors">Safari</Link>
          <Link href="/activities" className="text-[var(--primary)]">Activities</Link>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <Link href="/auth/login" className="text-sm text-[var(--muted-foreground)] hover:text-[var(--foreground)] px-4 py-2">Sign In</Link>
          <Link href="/auth/register" className="btn-primary text-sm px-5 py-2 rounded-lg">Join Now</Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050810] via-[var(--background)] to-[#0D1525]" />
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(ellipse 80% 60% at 50% 20%, rgba(201,168,76,0.15) 0%, transparent 70%)' }} />
        <div className="relative z-10 max-w-6xl mx-auto px-6">
          <p className="label-caps mb-3 text-[var(--primary)]">Activities & Experiences</p>
          <h1 className="text-4xl md:text-6xl font-bold mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
            400,000+ Experiences<br />
            <span className="gold-text-gradient">Worldwide.</span>
          </h1>
          <p className="text-lg text-[var(--muted-foreground)] mb-8 max-w-xl">
            Tours, activities, attractions, and experiences in every destination — curated for the discerning traveller.
          </p>

          {/* Search Widget */}
          <div className="glass-card rounded-2xl p-6 border border-[rgba(201,168,76,0.15)]">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <div className="md:col-span-2">
                <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">Search Experiences</label>
                <input
                  type="text"
                  placeholder="e.g. Louvre museum, desert safari, cooking class..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] placeholder-[var(--muted-foreground)] focus:outline-none focus:border-[var(--primary)]"
                />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">Destination</label>
                <input
                  type="text"
                  placeholder="City or country..."
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] placeholder-[var(--muted-foreground)] focus:outline-none focus:border-[var(--primary)]"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-[var(--muted-foreground)] border border-[var(--border)] hover:border-[rgba(201,168,76,0.3)] transition-all"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filters
              </button>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2 text-xs text-[var(--foreground)] focus:outline-none focus:border-[var(--primary)]"
              >
                {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>

              {[
                { label: 'Instant Confirm', state: isInstant, setter: setIsInstant },
                { label: 'Free Cancel', state: isFreeCancellation, setter: setIsFreeCancellation },
                { label: 'Luxury', state: isLuxury, setter: setIsLuxury },
                { label: 'Family', state: isFamilyFriendly, setter: setIsFamilyFriendly },
              ].map(({ label, state, setter }) => (
                <button
                  key={label}
                  onClick={() => { setter(!state); }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${state ? 'bg-[var(--primary)] text-[var(--primary-foreground)]' : 'text-[var(--muted-foreground)] border border-[var(--border)] hover:border-[rgba(201,168,76,0.3)]'}`}
                >
                  {label}
                </button>
              ))}
            </div>

            {showFilters && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 pt-3 border-t border-[var(--border)]">
                <div>
                  <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">Max Price (USD)</label>
                  <input
                    type="number"
                    placeholder="e.g. 500"
                    value={priceMax}
                    onChange={(e) => setPriceMax(e.target.value)}
                    className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2 text-sm text-[var(--foreground)] focus:outline-none focus:border-[var(--primary)]"
                  />
                </div>
                <div>
                  <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1">Travel Date</label>
                  <input
                    type="date"
                    value={travelDate}
                    onChange={(e) => setTravelDate(e.target.value)}
                    className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2 text-sm text-[var(--foreground)] focus:outline-none focus:border-[var(--primary)]"
                  />
                </div>
              </div>
            )}

            <button onClick={handleSearch} className="btn-primary w-full py-3 rounded-xl text-sm font-semibold">
              Search {searchResult ? `${searchResult.total.toLocaleString()}+` : ''} Experiences
            </button>
          </div>
        </div>
      </section>

      {/* Trending Destinations */}
      <section className="py-8 border-t border-[var(--border)]">
        <div className="max-w-6xl mx-auto px-6">
          <p className="label-caps mb-4">Trending Destinations</p>
          <div className="flex flex-wrap gap-2">
            {TRENDING_DESTINATIONS.map((d) => (
              <button
                key={d.name}
                onClick={() => handleDestinationClick(d)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium transition-all ${destination === d.name ? 'bg-[var(--primary)] text-[var(--primary-foreground)]' : 'text-[var(--muted-foreground)] border border-[var(--border)] hover:border-[rgba(201,168,76,0.3)]'}`}
              >
                <span>{d.flag}</span>{d.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-6 border-t border-[var(--border)]">
        <div className="max-w-6xl mx-auto px-6">
          <p className="label-caps mb-3">Browse by Category</p>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => { setQuery(c); fetchProducts(true); }}
                className="px-3 py-1.5 rounded-full text-xs font-medium text-[var(--muted-foreground)] border border-[var(--border)] hover:text-[var(--primary)] hover:border-[rgba(201,168,76,0.3)] transition-all"
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-10 border-t border-[var(--border)]">
        <div className="max-w-6xl mx-auto px-6">
          {/* Status bar */}
          <div className="flex items-center justify-between mb-6">
            <div>
              {searchResult ? (
                <p className="text-sm text-[var(--muted-foreground)]">
                  <span className="font-bold text-[var(--foreground)]">{searchResult.total.toLocaleString()}</span> experiences found
                  {destination && <span> in <span className="text-[var(--primary)]">{destination}</span></span>}
                </p>
              ) : (
                <p className="text-sm text-[var(--muted-foreground)]">Discover world-class experiences</p>
              )}
            </div>
            {isSampleData && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <div className="w-2 h-2 rounded-full bg-amber-500" />
                <span className="text-xs text-amber-400">Sample experiences — add Viator API key to activate 400,000+ live experiences</span>
              </div>
            )}
            {!isSampleData && viatorConfigured === false && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <div className="w-2 h-2 rounded-full bg-amber-500" />
                <span className="text-xs text-amber-400">Live catalogue pending activation</span>
              </div>
            )}
            {!isSampleData && viatorConfigured === true && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-xs text-emerald-400">Live Viator catalogue</span>
              </div>
            )}
          </div>

          {/* Sync Status Banner — show when configured but catalogue is empty */}
          {syncStatus?.configured && syncStatus.productCount === 0 && !isSampleData && (
            <div className="mb-6 p-4 rounded-xl border border-emerald-500/20 bg-emerald-500/5 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <span className="text-emerald-400 text-lg">✅</span>
                <div>
                  <p className="text-sm font-semibold text-emerald-400">Viator API Key Configured</p>
                  <p className="text-xs text-[var(--muted-foreground)]">
                    Your API key is active. Trigger a catalogue sync to import live Viator products into the database.
                  </p>
                </div>
              </div>
              <button
                onClick={handleTriggerSync}
                disabled={syncing}
                className="btn-primary px-4 py-2 rounded-lg text-xs font-semibold disabled:opacity-50 flex items-center gap-2 shrink-0"
              >
                {syncing ? (
                  <><span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />Syncing...</>
                ) : (
                  <>⟳ Sync Catalogue</>
                )}
              </button>
            </div>
          )}

          {/* Sync Status Banner — show last sync info when products exist */}
          {syncStatus?.configured && syncStatus.productCount > 0 && (
            <div className="mb-4 flex items-center justify-between p-3 rounded-lg bg-[var(--surface-3)] border border-[var(--border)]">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-xs text-[var(--muted-foreground)]">
                  <span className="text-emerald-400 font-semibold">{syncStatus.productCount.toLocaleString()} products</span> synced from Viator
                  {syncStatus.lastSyncedAt && <span> · Last updated {new Date(syncStatus.lastSyncedAt).toLocaleDateString()}</span>}
                </span>
              </div>
              <button
                onClick={handleTriggerSync}
                disabled={syncing}
                className="text-xs px-3 py-1 rounded-lg bg-[var(--surface-4)] hover:bg-[var(--border)] transition-colors disabled:opacity-50 flex items-center gap-1"
              >
                {syncing ? <span className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" /> : '⟳'}
                Incremental Sync
              </button>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="glass-card rounded-xl overflow-hidden animate-pulse">
                  <div className="h-44 bg-[var(--surface-3)]" />
                  <div className="p-4 space-y-2">
                    <div className="h-4 bg-[var(--surface-3)] rounded w-3/4" />
                    <div className="h-3 bg-[var(--surface-3)] rounded w-1/2" />
                    <div className="h-3 bg-[var(--surface-3)] rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Products grid */}
          {!loading && searchResult && searchResult.products.length > 0 && (
            <>
              {isSampleData && (
                <div className="mb-6 p-4 rounded-xl border border-amber-500/20 bg-amber-500/5 flex items-start gap-3">
                  <svg className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-xs font-semibold text-amber-400 mb-0.5">Showing Sample Experiences</p>
                    <p className="text-xs text-[var(--muted-foreground)]">
                      These are curated sample experiences. Add your <code className="text-amber-400 bg-[var(--surface-3)] px-1 rounded">VIATOR_API_KEY</code> in the{' '}
                      <Link href="/api-credentials" className="text-amber-400 underline hover:no-underline">Enterprise API Credentials</Link>{' '}
                      page to activate 400,000+ live experiences from the Viator catalogue.
                    </p>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {searchResult.products.map((product) => (
                  <ProductCard key={product.product_code} product={product} />
                ))}
              </div>

              {/* Pagination */}
              {searchResult.totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 rounded-lg text-sm border border-[var(--border)] text-[var(--muted-foreground)] hover:text-[var(--foreground)] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-[var(--muted-foreground)]">
                    Page {page} of {searchResult.totalPages}
                  </span>
                  <button
                    onClick={() => setPage(Math.min(searchResult.totalPages, page + 1))}
                    disabled={page === searchResult.totalPages}
                    className="px-4 py-2 rounded-lg text-sm border border-[var(--border)] text-[var(--muted-foreground)] hover:text-[var(--foreground)] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}

          {/* Empty state — credentials configured but catalogue not yet synced */}
          {!loading && viatorConfigured === true && searchResult?.products.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'var(--font-serif)' }}>Viator Credentials Configured</h3>
              <p className="text-sm text-[var(--muted-foreground)] mb-6 max-w-md mx-auto">
                Your Viator API key is saved. The product catalogue has not been synced yet. Trigger a sync from the Viator Admin Console to load live experiences.
              </p>
              <Link href="/enterprise-admin-platform" className="btn-primary px-6 py-2.5 rounded-xl text-sm inline-block">
                Go to Viator Admin Console
              </Link>
            </div>
          )}

          {/* Empty state — no results from search */}
          {!loading && !isSampleData && viatorConfigured !== true && searchResult?.products.length === 0 && (
            <div className="text-center py-20">
              <p className="text-[var(--muted-foreground)] mb-4">No experiences found for your search.</p>
              <button onClick={() => { setQuery(''); setDestination(''); fetchProducts(true); }} className="btn-primary px-6 py-2.5 rounded-xl text-sm">
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </section>

      <footer className="border-t border-[var(--border)] py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-xs text-[var(--muted-foreground)]">© 2026 Worldway Travels Group · Powered by Viator Partner API</div>
          <div className="flex items-center gap-6 text-xs text-[var(--muted-foreground)]">
            <Link href="/" className="hover:text-[var(--primary)] transition-colors">Home</Link>
            <Link href="/safari" className="hover:text-[var(--primary)] transition-colors">Safari</Link>
            <Link href="/tailor-made" className="hover:text-[var(--primary)] transition-colors">Tailor-Made</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
